import { Button, Card, Flex, Form, Input, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { type Customer, type FoodGroup } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { useMutation } from '../../../jsonapi-provider';

const SUCCESS_ADD_MESSAGE = 'FoodGroup added successfully';
const SUCCESS_UPDATE_MESSAGE = 'FoodGroup updated successfully';

interface FoodGroupFormProps {
  foodGroup: FoodGroup;
  onSuccess: (foodgroup: FoodGroup) => void;
  onCancel: () => void;
}

const FoodGroupForm = (props: FoodGroupFormProps): ReactNode => {
  const notification = useNotification();
  const [formInstance] = Form.useForm();
  const { foodGroup, onSuccess, onCancel } = props;
  const isEditMode = useMemo(() => !!foodGroup.id, [foodGroup.id]);
  const initialValues = useMemo(
    () => ({
      ...foodGroup,
    }),
    [foodGroup]
  );
  const [mutateFoodGroup, foodGroups] = useMutation<Customer>(
    foodGroup?.id ? ['food-group', foodGroup.id] : ['food-group']
  );

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit = (submitted: FoodGroup & Record<string, unknown>): void => {
    const foodGroupToSubmit = { ...submitted } satisfies FoodGroup;
    from(mutateFoodGroup(foodGroupToSubmit))
      .pipe(
        switchMap((res) =>
          res.error ? throwError(() => new Error(res.error?.title)) : of(res.data!)
        ),
        tap(() => {
          notification.success({
            message: ' ',
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
          });
        }),
        tap(onSuccess),
        catchError((error: Error) => {
          notification.error({
            message: '',
            description: error.message,
          });
          return EMPTY;
        })
      )
      .subscribe();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={formInstance}
          id="food-group-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? foodGroup.name : 'New Food Group'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button
                block
                htmlType="submit"
                type="primary"
                disabled={foodGroups.isLoading}
                loading={foodGroups.isLoading}
              >
                Save
              </Button>
              <Button block onClick={onCancel} disabled={foodGroups.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

export default FoodGroupForm;
